// src/templateManager/components/tabs/SecurityTab.jsx
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Group,
  Alert,
  Accordion,
  Badge
} from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import SecurityCategoryBuilder from '../builders/SecurityCategoryBuilder';

/**
 * SecurityTab - Configure security taxonomy
 * Manages threat agents, attack surfaces, and asset value assessment
 */
function SecurityTab() {
  const { watch } = useFormContext();

  const securityTaxonomy = watch('taxonomy.securityTaxonomy') || {};
  const securityCategories = securityTaxonomy.security || [];

  // Security category metadata
  const categoryInfo = [
    {
      index: 0,
      icon: '🎭',
      name: 'Threat Agents',
      description: 'Identify the type of threat agents involved'
    },
    {
      index: 1,
      icon: '🤖',
      name: 'Attack Surface - Generative AI',
      description: 'Generative-model specific attack vectors'
    },
    {
      index: 2,
      icon: '🎯',
      name: 'Attack Surface - Predictive AI',
      description: 'Predictive-model specific attack vectors'
    },
    {
      index: 3,
      icon: '💎',
      name: 'Asset Value',
      description: 'Assess the value and sensitivity of assets'
    }
  ];

  return (
    <Stack gap="lg">
      {/* Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        Security taxonomy defines the threat model for AI systems. Configure threat agents, attack surfaces,
        and asset value assessment criteria.
      </Alert>

      {/* Security Categories */}
      {securityCategories.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="md">
            <Text size="lg" c="dimmed">No security taxonomy configured</Text>
            <Text size="sm" c="dimmed">
              Security categories will be loaded from the template
            </Text>
          </Stack>
        </Card>
      ) : (
        <Accordion multiple defaultValue={securityCategories.map((_, i) => i.toString())}>
          {securityCategories.map((category, index) => {
            const info = categoryInfo[index] || { icon: '🔒', name: category.name, description: category.description };
            const subcategoryCount = category.schema?.subcategories?.length || 0;
            const optionCount = category.schema?.options?.length || 0;

            return (
              <Accordion.Item key={index} value={index.toString()}>
                <Accordion.Control>
                  <Group>
                    <Text size="lg">{info.icon}</Text>
                    <div style={{ flex: 1 }}>
                      <Group gap="xs">
                        <Text fw={600}>{category.name || info.name}</Text>
                        {subcategoryCount > 0 && (
                          <Badge size="sm" variant="light">
                            {subcategoryCount} subcategories
                          </Badge>
                        )}
                        {optionCount > 0 && (
                          <Badge size="sm" variant="light" color="green">
                            {optionCount} options
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" c="dimmed">
                        {category.description || info.description}
                      </Text>
                    </div>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <SecurityCategoryBuilder categoryIndex={index} />
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {/* Summary */}
      <Card withBorder p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Summary</Text>
          <Group gap="md">
            <Badge variant="dot">Categories: {securityCategories.length}</Badge>
            <Badge variant="dot" color="blue">
              Subcategories: {securityCategories.reduce((sum, cat) => sum + (cat.schema?.subcategories?.length || 0), 0)}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Security taxonomy includes threat agents, attack surfaces for different AI types, and asset value assessment
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}

export default SecurityTab;
